import { t } from './i18n/i18n';
import { formatClock } from './boosts';
import { fetchOffers, type OfferView } from '../screens/shop/shop-api';
import { discountPct, offerCardHtml } from '../screens/shop/offer-card';

/**
 * The "special offer" banner shown when the game opens: one featured offer (the biggest discount), once an
 * hour. "Seen" is stamped when the banner is SHOWN, not when it is dismissed, so reloading straight away
 * never nags. Storage is best effort (private mode throws), with an in-memory copy for the session.
 */

export const OFFER_BANNER_KEY = 'dic:offerBannerSeenAt';
export const OFFER_BANNER_INTERVAL_MS = 60 * 60 * 1000;

let seenInMemory = 0;

export function lastBannerSeenAt(): number {
  let stored = 0;
  try {
    stored = Number(localStorage.getItem(OFFER_BANNER_KEY)) || 0;
  } catch {
    // Storage unavailable — fall back to the in-memory stamp.
  }
  return Math.max(stored, seenInMemory);
}

export function markBannerSeen(now: number = Date.now()): void {
  seenInMemory = now;
  try {
    localStorage.setItem(OFFER_BANNER_KEY, String(now));
  } catch {
    // The in-memory copy still covers this session.
  }
}

/** Pure: has the banner been shown within the last hour? */
export function bannerSeenRecently(lastSeen: number, now: number = Date.now()): boolean {
  return lastSeen > 0 && now - lastSeen < OFFER_BANNER_INTERVAL_MS;
}

/** Pure: the offer worth leading with — the biggest discount among those still on sale and affordable to look at. */
export function featuredOffer(offers: OfferView[]): OfferView | null {
  const live = offers.filter((o) => o.onSale && (o.activeUntil === null) && o.usesLeft > 0);
  if (live.length === 0) return null;
  return live.reduce((best, o) => (discountPct(o) > discountPct(best) ? o : best));
}

function escapeHtml(value: string): string {
  const d = document.createElement('div');
  d.textContent = value;
  return d.innerHTML;
}

/** Starts the check. Returns a teardown that also closes a banner that is still open. */
export function initOfferBanner(): () => void {
  let disposed = false;
  let el: HTMLElement | null = null;
  let tick: number | null = null;

  function close(): void {
    if (tick !== null) window.clearInterval(tick);
    tick = null;
    document.removeEventListener('keydown', onKey);
    el?.remove();
    el = null;
  }
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }

  async function run(): Promise<void> {
    if (bannerSeenRecently(lastBannerSeenAt())) return;
    let res;
    try {
      res = await fetchOffers();
    } catch {
      return;
    }
    if (disposed || !res || !Array.isArray(res.offers)) return;
    const offer = featuredOffer(res.offers);
    if (!offer) return;

    markBannerSeen();
    const skew = Date.now() - new Date(res.now).getTime();
    const endsAt = new Date(res.windowEndsAt).getTime();

    el = document.createElement('div');
    el.className = 'offer-banner fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/75';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', t('offerBanner.eyebrow'));
    el.innerHTML = `
      <div class="offer-banner-panel popup-enter w-full max-w-sm flex flex-col gap-3">
        <div class="offer-banner-eyebrow">${t('offerBanner.eyebrow')} <span data-banner-clock>${t('offerBanner.endsIn', { time: formatClock(endsAt - (Date.now() - skew)) })}</span></div>
        ${offerCardHtml({ ...offer, onSale: true }, { balance: Number.MAX_SAFE_INTEGER, now: Date.now() - skew, variant: 'tile', escape: escapeHtml, formatAmount: (n) => n.toLocaleString('en-GB') }).replace(/<button[\s\S]*?<\/button>/, '')}
        <div class="flex gap-2">
          <button type="button" data-banner="dismiss" class="flex-1 min-h-11 px-3 rounded border border-border text-text-dim text-sm hover:text-text hover:border-gold touch-manipulation">${t('offerBanner.dismiss')}</button>
          <button type="button" data-banner="open" class="flex-1 min-h-11 px-3 rounded bg-gold text-bg font-button text-sm touch-manipulation">${t('offerBanner.cta')}</button>
        </div>
      </div>`;
    el.addEventListener('click', (e) => {
      const which = (e.target as HTMLElement).closest('[data-banner]')?.getAttribute('data-banner');
      if (which === 'open') {
        close();
        window.dispatchEvent(new CustomEvent('shell:open-tab', { detail: 'shop' }));
      } else if (which === 'dismiss' || e.target === el) {
        close();
      }
    });
    document.body.appendChild(el);
    document.addEventListener('keydown', onKey);
    (el.querySelector('[data-banner="open"]') as HTMLElement | null)?.focus();
    tick = window.setInterval(() => {
      const left = endsAt - (Date.now() - skew);
      const c = el?.querySelector('[data-banner-clock]');
      if (c) c.textContent = t('offerBanner.endsIn', { time: formatClock(left) });
    }, 1000);
  }

  // Let the first screen settle before interrupting it.
  const start = window.setTimeout(() => void run(), 1500);

  return () => {
    disposed = true;
    window.clearTimeout(start);
    close();
  };
}
